"use client";

import { getRouteApi } from "@tanstack/react-router";
import { isNil } from "lodash-es";
import { useCallback, useMemo } from "react";

import { yearBoundsFromParam, yearRangeToParam } from "@/lib/faceted-search-url";
import type { ALBUM_TYPES } from "@garimpa/lib/albumTypes";
import type { FORMAT_CATEGORIES } from "@garimpa/lib/formatGroups";

import { searchFilterSummary } from "./search-filter-summary";
import { searchStateValues } from "./search-state-values";

type SearchAlbumType = (typeof ALBUM_TYPES)[number];
type SearchFormat = (typeof FORMAT_CATEGORIES)[number];

export type SearchFilters = {
  query: string;
  genre?: string;
  style?: string;
  type?: SearchAlbumType;
  format?: SearchFormat;
  country?: string;
  origin?: string;
  yearFrom?: number;
  yearTo?: number;
  page: number;
  label: string[];
  company: string[];
};

export type FilterUpdates = Partial<{
  q: string | undefined;
  genre: string | undefined;
  style: string | undefined;
  type: string | undefined;
  format: string | undefined;
  country: string | undefined;
  origin: string | undefined;
  yearFrom: number | undefined;
  yearTo: number | undefined;
  page: number | undefined;
  label: string[] | undefined;
  company: string[] | undefined;
}>;

type FilterKey = keyof FilterUpdates;
type FilterValue = FilterUpdates[FilterKey];

const routeApi = getRouteApi("/_app/search/");

export function useSearchState() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const [yearFrom, yearTo] = useMemo(() => yearBoundsFromParam(search.year), [search.year]);

  const filters = useMemo<SearchFilters>(
    () => searchStateValues(search, yearFrom, yearTo),
    [search, yearFrom, yearTo],
  );

  const setFilter = useCallback(
    (key: FilterKey, value: FilterValue, options?: { replace?: boolean }) => {
      trackFilterChange(key);
      navigate({
        search: (prev) => ({
          ...prev,
          ...routeUpdatesForFilter(filters, key, value),
          page: getPageForFilterUpdate(key, value),
        }),
        replace: options?.replace ?? false,
        resetScroll: false,
      });
    },
    [filters, navigate],
  );

  const setFilters = useCallback(
    (updates: FilterUpdates) => {
      const hasNonPageChange = Object.keys(updates).some((key) => key !== "page");
      const trackedKey = Object.keys(updates).find((key) => key !== "page") as
        | FilterKey
        | undefined;
      if (trackedKey) trackFilterChange(trackedKey);

      navigate({
        search: (prev) => ({
          ...prev,
          ...routeUpdatesForFilters(filters, updates),
          page: getPageForFilterUpdates(prev.page, updates, hasNonPageChange),
        }),
        replace: false,
        resetScroll: false,
      });
    },
    [filters, navigate],
  );

  const resetFilters = useCallback(() => {
    const currentQuery = filters.query;
    navigate({
      search: () => ({
        q: currentQuery || undefined,
        genre: undefined,
        style: undefined,
        type: undefined,
        format: undefined,
        country: undefined,
        origin: undefined,
        year: undefined,
        page: undefined,
        label: undefined,
        company: undefined,
      }),
      replace: false,
      resetScroll: false,
    });
  }, [navigate, filters.query]);

  const { musicHasFilters, hasFilters, activeFilterCount } = searchFilterSummary(filters);

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    musicHasFilters,
    hasFilters,
    activeFilterCount,
  };
}

function routeUpdatesForFilter(filters: SearchFilters, key: FilterKey, value: FilterValue) {
  if (key === "yearFrom" || key === "yearTo") {
    const nextYearFrom = key === "yearFrom" ? (value as number | undefined) : filters.yearFrom;
    const nextYearTo = key === "yearTo" ? (value as number | undefined) : filters.yearTo;
    return { year: yearRangeToParam(nextYearFrom, nextYearTo) };
  }

  return { [key]: cleanValue(value) };
}

function routeUpdatesForFilters(filters: SearchFilters, updates: FilterUpdates) {
  const normalized: Record<string, unknown> = {};
  const hasYearUpdate = "yearFrom" in updates || "yearTo" in updates;

  for (const [key, value] of Object.entries(updates)) {
    if (key === "yearFrom" || key === "yearTo") continue;
    normalized[key] = cleanValue(value);
  }

  if (hasYearUpdate) {
    normalized.year = yearRangeToParam(
      "yearFrom" in updates ? updates.yearFrom : filters.yearFrom,
      "yearTo" in updates ? updates.yearTo : filters.yearTo,
    );
  }

  return normalized;
}

function cleanValue(value: unknown) {
  if (Array.isArray(value)) return value.length ? value : undefined;
  return isNil(value) || value === "" ? undefined : value;
}

function getPageForFilterUpdate(key: FilterKey, value: FilterValue) {
  if (key !== "page") {
    return undefined;
  }

  return value as number | undefined;
}

function getPageForFilterUpdates(
  currentPage: number | undefined,
  updates: FilterUpdates,
  hasNonPageChange: boolean,
) {
  if (hasNonPageChange) {
    return undefined;
  }

  if ("page" in updates) {
    return updates.page;
  }

  return currentPage;
}

function trackFilterChange(key: FilterKey) {
  const filterKey = getAnalyticsFilterKey(key);
  if (!filterKey) return;
}

function getAnalyticsFilterKey(key: FilterKey): string | null {
  if (key === "q" || key === "page") return null;
  if (key === "yearFrom" || key === "yearTo") return "year";
  if (key === "origin") return "artistCountry";
  return key as string;
}
